"use client";

import {
  CustomButton,
  FlexBetweenCol,
  Logo,
} from "@/components";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import HtmlRenderer from "@/components/htmlRenderer";
import Loader from "@/components/ui/loader";
import Dropdown from "@/components/ui/dropdown/dropdown";
import { noNavigateServices } from "@/constants";
import { plateAlphabet } from "@/constants/plateList";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import { useFreewayTollInquiryMutation } from "@/hooks/useInquiry";
import {
  IServiceFormProps,
  TFreewayToll,
  TRawServiceApiKeys,
} from "@/interfaces";
import { IFreewayTollInquiryPayload } from "@/interfaces/inquiries/vehicle";
import { routes } from "@/routes";
import { useAppStore, useModalStore } from "@/store";
import { freewayTollInquirySchema } from "@/validations";
import { zodResolver } from "@hookform/resolvers/zod";
import MainLogo from "@public/svg/logo-2.svg";
import CarLicenseImg from "public/carLicense.png";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import InquiryCost from "../../inquiryCost";

const FreewayTollInquiryForm = ({
  cost,
  isLoggedIn,
  navigateToLogin,
  announcement,
  disableService,
  serviceId,
}: IServiceFormProps) => {
  const { data: session } = useSession();
  const { setPostLoginAction } = useAppStore();
  const router = useRouter();
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const isPaymentMethodModalOpen = openModal === "payment-method";
  const {
    isPending: isSubmitting,
    mutate: freewayTollFn,
    error: freewayTollError,
    reset: resetFreewayToll,
  } = useFreewayTollInquiryMutation(session?.user.token);
  const t = useTranslations();
  const serviceKey = TRawServiceApiKeys.Freeway_Toll;
  const activeKey = noNavigateServices.find(
    (item) => item.key === globalRawServicesData.Freeway_Toll.key,
  )!;
  const inputRefs = useRef<(HTMLInputElement | HTMLSelectElement | null)[]>([]);
  const [packageId, setPackageId] = useState<string | null>(null);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerEndTime, setTimerEndTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors: formError },
  } = useForm<TFreewayToll>({
    resolver: zodResolver(freewayTollInquirySchema),
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number,
    maxLength: number,
  ) => {
    const value = e.target.value;

    if (value.length >= maxLength && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleBackspace = (
    e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
    index: number,
  ) => {
    const value = (e.target as HTMLInputElement).value;
    if (e.key === "Backspace" && value === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleDropdownChange = (
    value: string,
    index: number,
    maxLength: number,
  ) => {
    if (value.length >= maxLength && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const extractLetter = (value: string) => {
    if (!value) return value;
    return value.split("(")[0].trim();
  };

  const onSubmit = () => {
    setError(null);
    if (!isLoggedIn) {
      setPostLoginAction("open-payment-modal");
      navigateToLogin();
      return;
    }
    setOpenModal("payment-method");
  };

  const paymentSubmission = () => {
    const formData = getValues();
    const payload: Readonly<IFreewayTollInquiryPayload> = {
      plk1: formData.plk1,
      plk2: extractLetter(formData.plk2),
      plk3: formData.plk3,
      plk_serial: formData.plk_serial,
      campaign: packageId || undefined,
    };

    freewayTollFn(
      { payload },
      {
        onSuccess: (data) => {
          const purchaseId = data.data?.purchase_id;
          if (purchaseId) {
            router.push(`${routes.main.INQUIRY_DETAILS.primaryPath}/${purchaseId}`);
            closeModal();
          }
          setError(null);
          setIsTimerActive(false);
          setTimerEndTime(null);
        },
        onError: (submissionError) => {
          setError(submissionError.message);
          setIsTimerActive(true);
          setTimerEndTime(Date.now() + 60000);
        },
      },
    );
  };

  const searchParams = useSearchParams();
  useEffect(() => {
    if (searchParams?.get("payment") === "1") {
      setOpenModal("payment-method");
    }
  }, [searchParams, setOpenModal]);

  useEffect(() => {
    if (isPaymentMethodModalOpen) {
      resetFreewayToll();
    }
  }, [isPaymentMethodModalOpen, resetFreewayToll]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexBetweenCol gap={8} className="w-full">
          <div className="w-full">
            <div
              dir="ltr"
              className="shadow-main flex max-h-[45px] w-full rounded-lg border bg-white text-center lg:max-h-[56px]"
            >
              <div className="flex w-[20px] items-center justify-center border-r lg:w-[25px]">
                <Image
                  src={CarLicenseImg}
                  alt="شماره پلاک"
                  className="h-full w-full object-cover"
                  title="شماره پلاک"
                />
              </div>

              <div className="flex w-[60px] flex-col items-center justify-end border-r lg:w-[85px]">
                <Controller
                  name="plk1"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      {...field}
                      maxLength={2}
                      placeholder="_ _"
                      ref={(el) => {
                        field.ref(el);
                        inputRefs.current[0] = el;
                      }}
                      onChange={(e) => {
                        field.onChange(e);
                        handleChange(e, 0, 2);
                      }}
                      onKeyDown={(e) => handleBackspace(e, 0)}
                      className="placeholder:font-extrablack h-7 w-10 rounded border !text-center text-[12px] placeholder:text-gray-400 sm:h-9 lg:w-16 lg:text-lg"
                    />
                  )}
                />
              </div>

              <div className="flex w-[70px] items-center border-r sm:w-[95px] lg:w-[130px]">
                <Controller
                  name="plk2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Dropdown
                      items={plateAlphabet}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        handleDropdownChange(value, 1, 1);
                      }}
                      placeholder="انتخاب حرف"
                      variant="flat"
                      className="size-full! h-10! lg:h-10! xl:h-12!"
                    />
                  )}
                />
              </div>

              <div className="flex w-[60px] flex-col items-center justify-end border-r sm:w-[80px] lg:w-[125px]">
                <Controller
                  name="plk3"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      {...field}
                      maxLength={3}
                      placeholder="_ _ _"
                      ref={(el) => {
                        field.ref(el);
                        inputRefs.current[2] = el;
                      }}
                      onChange={(e) => {
                        field.onChange(e);
                        handleChange(e, 2, 3);
                      }}
                      onKeyDown={(e) => handleBackspace(e, 2)}
                      className="placeholder:font-extrablack h-7 w-10 rounded border !text-center text-[12px] placeholder:text-gray-400 sm:h-9 sm:w-16 lg:w-20 lg:text-lg"
                    />
                  )}
                />
              </div>

              <div className="mx-auto flex w-[60px] flex-col items-center justify-center rounded-r-lg lg:w-[85px]">
                <label
                  htmlFor="plk_serial"
                  className="text-micro lg:text-[12px]"
                >
                  ایران
                </label>
                <Controller
                  name="plk_serial"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      {...field}
                      id="plk_serial"
                      maxLength={2}
                      placeholder="_ _"
                      ref={(el) => {
                        field.ref(el);
                        inputRefs.current[3] = el;
                      }}
                      onChange={(e) => {
                        field.onChange(e);
                        handleChange(e, 3, 2);
                      }}
                      onKeyDown={(e) => handleBackspace(e, 3)}
                      className="placeholder:font-extrablack h-9 w-10 rounded border !text-center text-[12px] placeholder:text-gray-400 lg:w-16 lg:text-lg"
                    />
                  )}
                />
              </div>
            </div>
            {(formError.plk1 || formError.plk2 || formError.plk3) && (
              <p className="text-error-main mt-2 w-full text-xs">
                {t("validation.plateNoNotValid")}
              </p>
            )}
          </div>

          {session?.user.token && <InquiryCost cost={cost} />}
          {announcement && <HtmlRenderer htmlString={announcement} />}

          <CustomButton
            id="freeway-toll-inquiry-submit"
            disabled={disableService}
            className="w-full"
            variant="primary"
            type="submit"
          >
            استعلام عوارض آزادراهی
          </CustomButton>
        </FlexBetweenCol>
      </form>

      {isPaymentMethodModalOpen && (
        <PaymentModalContainer
          payBtnId="payment-freeway-toll-inquiry-submit"
          submissionError={freewayTollError}
          paymentSubmission={paymentSubmission}
          isOpen={isPaymentMethodModalOpen}
          onClose={closeModal}
          selectedService={activeKey}
          submissionLoading={isSubmitting}
          onErrorReset={resetFreewayToll}
          onPackageSelect={setPackageId}
          serviceId={serviceId}
          showData="service"
          persistedData={{
            [serviceKey]: {
              ...getValues(),
            },
          }}
          showManualError={error}
          isTimerActive={isTimerActive}
          timerEndTime={timerEndTime}
          onTimerComplete={() => {
            setIsTimerActive(false);
            setTimerEndTime(null);
          }}
        />
      )}

      {isSubmitting && (
        <div className="flex-center fixed inset-0 z-[1000] flex bg-gray-900/60">
          <div className="flex-column flex-center flex w-60 rounded-xl bg-white p-3 sm:w-80 sm:p-4">
            <Logo className="mb-3" src={MainLogo} width={60} height={60} />
            <h2 className="text-center text-sm font-semibold sm:text-base">
              درحال استعلام <br /> لطفا شکیبا باشید
            </h2>
            <div className="mt-4 size-4 sm:size-5">
              <Loader color="bg-primary-main" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FreewayTollInquiryForm;
